import type {
  CentAmount,
  ProductSearchQuery,
  TransformedFilter,
} from '@scayle/storefront-nuxt'

export enum FilterTypes {
  ATTRIBUTES = 'attributes',
  PRICE = 'price',
  CATEGORY = 'category',
  BRAND = 'brand',
}

export interface AttributesFilterValue {
  name: string
  id: number
  productCount: number
  value: string
}

export interface AttributesFilterItemWithValues {
  id: number | null
  slug: string
  name: string
  values: AttributesFilterValue[]
  type: FilterTypes.ATTRIBUTES
  attributeGroupType: string
}

export type RangeFilterValue = [
  {
    min: CentAmount
    max: CentAmount
    productCount: number
  },
]

export enum SizeCategoryIdentifier {
  NGROESSEN = 'ngroessen',
  KGROESSEN = 'kgroessen',
  EKGROESSEN = 'ekgroessen',
  LGROESSEN = 'lgroessen',
  GROESSEN = 'groessen',
  MIEDERGROESSEN_STANDARDLAUF = 'miedergroessen_standardlauf',
  MIEDERGROESSEN_GESAMT = 'miedergroessen_gesamt',
  SCHUHGROESSEN = 'schuhgroessen',
  KOERBCHENGROESSEN = 'koerbchengroessen',
  BADEMODENGROESSEN = 'bademodengroessen',
  UNKNOWN = 'unknown',
}

// Parses a SCAYLE shopLevelCustomData.sizeCategorySortOrder value into a
// validated SizeCategoryIdentifier list. Accepts either a comma-separated
// string ("kgroessen,schuhgroessen,…") or an array; unknown entries are
// dropped so a partial override never corrupts the list.
export const parseSizeCategorySortOrder = (
  override: string | string[] | undefined | null,
): SizeCategoryIdentifier[] => {
  if (!override) return []

  const rawEntries = Array.isArray(override) ? override : override.split(',')
  const validIdentifiers = new Set<string>(
    Object.values(SizeCategoryIdentifier),
  )

  return rawEntries.reduce<SizeCategoryIdentifier[]>((acc, entry) => {
    const normalized = entry?.toString().trim().toLowerCase()
    if (normalized && validIdentifiers.has(normalized)) {
      acc.push(normalized as SizeCategoryIdentifier)
    }
    return acc
  }, [])
}

// Sort non-numeric values
const ORDER = [
  'XXS',
  'XS',
  'S',
  'S/M',
  'M',
  'L',
  'L/XL',
  'XL',
  'XXL',
  'XXXL',
  '3XL',
  '4XL',
  '5XL',
]

export const sortSizeFilterValues = (items: TransformedFilter[]) => {
  const patternSlash = /^[0-9]+\/[0-9]+$/
  const patternComma = /^[0-9]+,[0-9]+$/
  return items
    .map((item: TransformedFilter) => {
      if (patternSlash.test(item.displayName)) {
        return { name: item.displayName.split('/').join('.'), ...item }
      }
      if (patternComma.test(item.displayName)) {
        return { name: item.displayName.split(',').join('.'), ...item }
      }

      return { name: item.displayName, ...item }
    })
    .sort((a, b) => {
      if (!isNaN(Number(a.name)) && !isNaN(Number(b.name))) {
        return Number(a.name) < Number(b.name) ? -1 : 1
      }
      if (!isNaN(Number(a.name)) && isNaN(Number(b.name))) {
        return -1
      }
      if (isNaN(Number(a.name)) && !isNaN(Number(b.name))) {
        return 1
      }
      return ORDER.indexOf(a.name) - ORDER.indexOf(b.name)
    })
}

const parseCupSize = (
  size: string,
): {
  prefix: number | null
  suffix: string
  isCupSize: boolean
} => {
  const cupSizePattern = /^(\d+)\s*([A-Za-z]+)$/
  const match = size.trim().match(cupSizePattern)

  if (match) {
    const prefix = match[1]
    const suffix = match[2]
    if (prefix === undefined || suffix === undefined) {
      return {
        prefix: null,
        suffix: '',
        isCupSize: false,
      }
    }

    return {
      prefix: Number(prefix),
      suffix: suffix.toUpperCase(),
      isCupSize: true,
    }
  }

  return {
    prefix: null,
    suffix: '',
    isCupSize: false,
  }
}

const parseEuropeanNumericSize = (
  size: string,
): {
  isCombined: boolean
  firstValue: number | null
  secondValue: number | null
  isNumeric: boolean
} => {
  const combinedPattern = /^(\d+)\/(\d+)$/
  const singlePattern = /^(\d+)$/

  const combinedMatch = size.match(combinedPattern)
  if (combinedMatch) {
    return {
      isCombined: true,
      firstValue: Number(combinedMatch[1]),
      secondValue: Number(combinedMatch[2]),
      isNumeric: true,
    }
  }

  const singleMatch = size.match(singlePattern)
  if (singleMatch) {
    return {
      isCombined: false,
      firstValue: Number(singleMatch[1]),
      secondValue: null,
      isNumeric: true,
    }
  }

  return {
    isCombined: false,
    firstValue: null,
    secondValue: null,
    isNumeric: false,
  }
}

export const compareCupSizes = (
  sizeA: string | null | undefined,
  sizeB: string | null | undefined,
): number => {
  if (!sizeA || !sizeB) return 0

  const parsedA = parseCupSize(sizeA.trim())
  const parsedB = parseCupSize(sizeB.trim())

  if (!parsedA.isCupSize || !parsedB.isCupSize) {
    return 0
  }

  if (parsedA.prefix === null || parsedB.prefix === null) {
    return 0
  }

  if (parsedA.prefix !== parsedB.prefix) {
    return parsedA.prefix - parsedB.prefix
  }

  const suffixA = parsedA.suffix
  const suffixB = parsedB.suffix
  const firstSuffixA = suffixA[0]
  const firstSuffixB = suffixB[0]

  if (firstSuffixA === undefined || firstSuffixB === undefined) {
    return 0
  }

  if (suffixA === suffixB) {
    return 0
  }

  const isDoubleA = suffixA.length > 1 && suffixA[0] === suffixA[1]
  const isDoubleB = suffixB.length > 1 && suffixB[0] === suffixB[1]

  if (isDoubleA && isDoubleB) {
    return suffixA.localeCompare(suffixB)
  }

  if (isDoubleA && !isDoubleB) {
    if (firstSuffixA === suffixB) {
      return 1
    }
    return firstSuffixA.localeCompare(suffixB)
  }

  if (!isDoubleA && isDoubleB) {
    if (suffixA === firstSuffixB) {
      return -1
    }
    return suffixA.localeCompare(firstSuffixB)
  }

  return suffixA.localeCompare(suffixB)
}

export const compareEuropeanNumericSizes = (
  sizeA: string | null | undefined,
  sizeB: string | null | undefined,
): number => {
  if (!sizeA || !sizeB) return 0

  const parsedA = parseEuropeanNumericSize(sizeA.trim())
  const parsedB = parseEuropeanNumericSize(sizeB.trim())

  if (!parsedA.isNumeric || !parsedB.isNumeric) {
    return 0
  }

  if (parsedA.firstValue === null || parsedB.firstValue === null) {
    return 0
  }

  const numA = parsedA.firstValue
  const numB = parsedB.firstValue

  if (parsedA.isCombined && parsedB.isCombined) {
    if (numA === numB) {
      return (parsedA.secondValue || 0) - (parsedB.secondValue || 0)
    }
    return numA - numB
  }

  if (parsedA.isCombined && !parsedB.isCombined) {
    if (numA === numB) {
      return 1
    }
    if (parsedA.secondValue === numB) {
      return -1
    }
    return numA - numB
  }

  if (!parsedA.isCombined && parsedB.isCombined) {
    if (numA === numB) {
      return -1
    }
    if (numA === parsedB.secondValue) {
      return 1
    }
    return numA - numB
  }

  return numA - numB
}

const ALPHABETIC_SIZE_ORDER: Record<string, number> = {
  XXS: 0,
  XS: 1,
  S: 2,
  M: 3,
  L: 4,
  XL: 5,
  XXL: 6,
  XXXL: 7,
  '3XL': 7,
  '4XL': 8,
  '5XL': 9,
}

/**
 * Ranks an alphabetic size label (XXS…5XL). Combined sizes like "S/M" or
 * "L/XL" rank at the midpoint of their parts so they interleave correctly
 * with single sizes: S < S/M < M … L < L/XL < XL.
 */
const getAlphabeticSizeRank = (
  label: string | null | undefined,
): number | undefined => {
  if (!label) {
    return undefined
  }

  const normalized = label.trim().toUpperCase()
  const direct = ALPHABETIC_SIZE_ORDER[normalized]
  if (direct !== undefined) {
    return direct
  }

  const [firstPart, secondPart, ...remainingParts] = normalized
    .split('/')
    .map((part) => part.trim())
  if (
    firstPart === undefined ||
    secondPart === undefined ||
    remainingParts.length > 0
  ) {
    return undefined
  }

  const firstRank = ALPHABETIC_SIZE_ORDER[firstPart]
  const secondRank = ALPHABETIC_SIZE_ORDER[secondPart]
  if (firstRank === undefined || secondRank === undefined) {
    return undefined
  }

  return (firstRank + secondRank) / 2
}

/**
 * Compares two alphabetic size labels (XS, M, XL, S/M, L/XL…).
 * Returns 0 when either label is not an alphabetic size, so callers can
 * fall through to the next comparison strategy.
 */
export const compareAlphabeticSizes = (
  sizeA: string | null | undefined,
  sizeB: string | null | undefined,
): number => {
  const rankA = getAlphabeticSizeRank(sizeA)
  const rankB = getAlphabeticSizeRank(sizeB)

  if (rankA === undefined || rankB === undefined) {
    return 0
  }

  return rankA - rankB
}

export const sortInternationalSizes = (
  values: { displayName: string }[],
): { displayName: string }[] => {
  return values
    .map((item, index) => ({ ...item, _originalIndex: index }))
    .sort((a, b) => {
      const extractSize = (val: string) => (val.split('|')[1] || val).trim()

      const sizeA = extractSize(a.displayName)
      const sizeB = extractSize(b.displayName)

      const cupSizeComparison = compareCupSizes(sizeA, sizeB)
      if (cupSizeComparison !== 0) {
        return cupSizeComparison
      }

      const comparison = compareEuropeanNumericSizes(sizeA, sizeB)
      if (comparison !== 0) {
        return comparison
      }

      const sizeAUpper = sizeA.toUpperCase()
      const sizeBUpper = sizeB.toUpperCase()
      const isIntlA = ORDER.includes(sizeAUpper)
      const isIntlB = ORDER.includes(sizeBUpper)

      // Convert comma decimal separator to period for numeric check
      const numericSizeA = sizeA.replace(',', '.')
      const numericSizeB = sizeB.replace(',', '.')
      const isNumericA = !isNaN(Number(numericSizeA))
      const isNumericB = !isNaN(Number(numericSizeB))

      // Handle numeric sizes (shoe sizes)
      if (isNumericA && isNumericB) {
        const numA = Number(numericSizeA)
        const numB = Number(numericSizeB)

        // Sort by category (EU=0, US=1), then by numeric value
        const categoryA = numA > 17 ? 0 : 1
        const categoryB = numB > 17 ? 0 : 1

        return categoryA - categoryB || numA - numB
      }
      if (isNumericA && !isNumericB) return -1
      if (!isNumericA && isNumericB) return 1

      if (isIntlA && !isIntlB) return 1
      if (!isIntlA && isIntlB) return -1

      // Handle international sizes
      if (isIntlA && isIntlB) {
        return ORDER.indexOf(sizeAUpper) - ORDER.indexOf(sizeBUpper)
      }

      // fallback: preserve original order
      return a._originalIndex - b._originalIndex
    })
    .map(({ _originalIndex, ...rest }) => rest)
}

export const transformWhereConditionToMap = (
  whereCondition: ProductSearchQuery,
): Record<string, any> => {
  const { attributes, ...rest } = whereCondition

  const transformedAttributes = attributes?.reduce(
    (acc, attribute) => {
      if (attribute.key) {
        if (attribute.type === 'attributes') {
          acc[attribute.key] = attribute.values
        } else if (attribute.type === 'boolean') {
          acc[attribute.key] = attribute.value.toString()
        }
      }
      return acc
    },
    {} as Record<string, any>,
  )

  return {
    ...rest,
    ...transformedAttributes,
  }
}

/**
 * SCAYLE's `getProductsByCategory` / `getFilters` RPCs run `where.attributes`
 * through `sanitizeAttributesForSAPI`, which keeps an attribute only when its
 * key is a known category filter AND is *not* listed in the request's
 * `includedFilters` (`sale`, `isNew`, `styleGroup`). Any applied quick filter
 * whose key is in `includedFilters` is therefore stripped before reaching SAPI
 * and is silently ignored — the listing/search/count comes back unfiltered.
 *
 * `whitelistAttributes` is concatenated onto the query *after* sanitization, so
 * routing the included quick filters through it makes them actually apply. This
 * splits a `where` accordingly: included quick filters go to
 * `whitelistAttributes`, everything else stays in `attributes`.
 */
export const withWhitelistedQuickFilters = (
  where: ProductSearchQuery,
  includedFilters: readonly string[],
): ProductSearchQuery & {
  whitelistAttributes?: ProductSearchQuery['attributes']
} => {
  const includedKeys = new Set(includedFilters.map((key) => key.toLowerCase()))
  const { attributes = [], ...rest } = where

  const isIncludedQuickFilter = (attribute: { key?: string }) =>
    !!attribute.key && includedKeys.has(attribute.key.toLowerCase())

  const whitelistAttributes = attributes.filter(isIncludedQuickFilter)

  return {
    ...rest,
    attributes: attributes.filter((a) => !isIncludedQuickFilter(a)),
    ...(whitelistAttributes.length ? { whitelistAttributes } : {}),
  }
}

export default {
  sortSizeFilterValues,
}
